import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Badge } from 'src/@/components/ui/badge';
import Iconify from 'src/components/Iconify';
import propostasService from 'src/services/propostasService';
import { PROPOSTAS_DESIGN } from 'src/pages/dashboard/Propostas/Propostas.design';

function formatCurrency(value) {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

ChatInfoPropostas.propTypes = {
    chat: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    onCountUpdate: PropTypes.func,
    refreshTrigger: PropTypes.number,
    onNovaProposta: PropTypes.func,
    onAbrirProposta: PropTypes.func,
};

// Criar e abrir acontecem em modal (PropostaRapidaDialog), montado pela sidebar:
// o vendedor preenche um formulário sem sair da conversa e sem ver o documento.
export default function ChatInfoPropostas({ chat, user, onCountUpdate, refreshTrigger, onNovaProposta, onAbrirProposta }) {
    const { t } = useTranslation();
    const [propostas, setPropostas] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setPropostas([]);

        (async () => {
            if (!chat?._id || !user?.clienteId) return;
            setLoading(true);
            try {
                const response = await propostasService.listar(user.clienteId, { chat_id: chat._id, limit: 100 });
                const data = response.data || [];
                if (cancelled) return;
                setPropostas(data);
                if (onCountUpdate) onCountUpdate(data.length);
            } catch (error) {
                if (!cancelled) setPropostas([]);
                console.log('Erro ao buscar propostas do chat', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [chat?._id, refreshTrigger]);

    const renderStatusBadge = (status) => {
        const cfg = PROPOSTAS_DESIGN.status[status] || PROPOSTAS_DESIGN.status.rascunho;
        return (
            <Badge variant="outline" className={`text-[10px] font-medium border-0 ${cfg.bg} ${cfg.text}`}>
                {cfg.label}
            </Badge>
        );
    };

    return (
        <div className="space-y-2">
            <button
                onClick={onNovaProposta}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-[#7C3AED] bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors"
            >
                <Iconify icon="eva:plus-outline" width={14} />
                {t('chat.info.proposals.create', 'Criar proposta')}
            </button>

            {loading && propostas.length === 0 && (
                <div className="text-center py-4">
                    <p className="text-xs text-gray-400">{t('common.buttons.loading', 'Carregando...')}</p>
                </div>
            )}

            {!loading && propostas.length === 0 && (
                <div className="text-center py-4">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-2">
                        <Iconify icon="mdi:file-document-outline" width={20} className="text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('chat.info.proposals.empty', 'Nenhuma proposta vinculada a este chat.')}</p>
                </div>
            )}

            {propostas.length > 0 && (
                <div className="space-y-2">
                    {propostas.map((proposta) => (
                        <div
                            key={proposta._id}
                            onClick={() => onAbrirProposta(proposta._id)}
                            className="bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl hover:border-violet-200 dark:hover:border-violet-600 hover:shadow-sm cursor-pointer transition-all p-3 space-y-1.5"
                        >
                            <div className="flex items-center justify-between gap-2">
                                {renderStatusBadge(proposta.status)}
                                <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">
                                    {formatCurrency(proposta.valor_total)}
                                </span>
                            </div>
                            <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-200 leading-snug line-clamp-2">
                                {proposta.titulo}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
